import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  CreditCard as PaymentIcon,
  Description as InvoiceIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { DateTime } from 'luxon';
import useAxiosPrivate from "../../Hooks/useAxiosPrivate";
import styles from "./MyReserved.module.css";

const moneyFormat = (value) => {
  const n = Number(value ?? 0);
  if (Number.isNaN(n)) return "$0.00";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const dateFormat = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const orderDetailDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const STATUS_COLORS = {
  New: "default",
  "Partial Payment": "warning",
  "Payment Received": "success",
  Refunded: "info",
  Cancelled: "error",
};

// ─── Order Detail Modal (matches admin /admin/finances/:id — no audit log) ───
const OrderDetailModal = ({ open, orderId, attendeeId, onClose }) => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !orderId) return;
    let cancelled = false;
    setDetail(null);
    setError(false);
    setLoading(true);

    axiosPrivate
      .get(`/api/orders/${orderId}`)
      .then((r) => {
        if (!cancelled) setDetail(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, orderId, axiosPrivate]);

  const buildBillingPrefill = (customer) => {
    if (!customer) return null;
    return {
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      address: customer.address || "",
      apt: customer.apt || "",
      city: customer.city || "",
      state: customer.state || "NC",
      zip: customer.zip || "",
    };
  };

  const handleApplyPayment = () => {
    const summary = detail?.summary;
    const customer = detail?.customer;
    const remaining = Number(summary?.remainingBalance ?? 0);
    const billingPrefill = buildBillingPrefill(customer);
    const fullName = [billingPrefill?.firstName, billingPrefill?.lastName].filter(Boolean).join(" ").trim();

    onClose();
    navigate(`/makepayment/${attendeeId}`, {
      state: {
        billingPrefill,
        nameOnCardPrefill: fullName,
        suggestedAmount: remaining > 0 ? remaining : undefined,
        fromOrderView: true,
        orderId,
      },
    });
  };

  const summary = detail?.summary;
  const order = detail?.order;
  const customer = detail?.customer;
  const orderItems = detail?.orderItems || [];
  const paymentHistory = detail?.paymentHistory || [];
  const status = order?.Status || "New";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          bgcolor: "#003063",
          color: "#fff",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold" color="inherit">
            Order #{orderId}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Order details
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, bgcolor: "#fafafa" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Typography color="error" sx={{ py: 2 }}>
            Failed to load order details. Please try again.
          </Typography>
        )}

        {!loading && detail && (
          <Stack spacing={2}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#003063" }}>
                Order Summary
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 3,
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {customer?.name || "N/A"}
                  </Typography>
                  {customer?.email && (
                    <Typography variant="body2" color="text.secondary">
                      {customer.email}
                    </Typography>
                  )}
                  {customer?.churchName && (
                    <Typography variant="body2" color="text.secondary">
                      {customer.churchName}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={status}
                    color={STATUS_COLORS[status] || "default"}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Order Date
                  </Typography>
                  <Typography variant="body1">{orderDetailDateTime(order?.OrderDate)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Event
                  </Typography>
                  <Typography variant="body1">{order?.EventName || order?.Products || "—"}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  gap: 2,
                }}
              >
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#f8f9fa" }} elevation={0}>
                  <Typography variant="caption" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {moneyFormat(summary?.grandTotal)}
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#e8f5e9" }} elevation={0}>
                  <Typography variant="caption" color="text.secondary">
                    Total Paid
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {moneyFormat(summary?.totalPaid)}
                  </Typography>
                </Paper>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: Number(summary?.remainingBalance) > 0 ? "#fbe9e7" : "#e8f5e9",
                  }}
                  elevation={0}
                >
                  <Typography variant="caption" color="text.secondary">
                    Remaining Balance
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={Number(summary?.remainingBalance) > 0 ? "error.main" : "success.main"}
                  >
                    {moneyFormat(summary?.remainingBalance)}
                  </Typography>
                </Paper>
              </Box>
            </Paper>

            {orderItems.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#003063" }}>
                  Order Items
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Description</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Qty</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Unit Price</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Total</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderItems.map((item) => (
                        <TableRow key={item.OrderItemID}>
                          <TableCell>{item.Description || item.ProductName || "—"}</TableCell>
                          <TableCell align="right">{item.Quantity ?? 1}</TableCell>
                          <TableCell align="right">{moneyFormat(item.UnitPrice)}</TableCell>
                          <TableCell align="right">{moneyFormat(item.TotalPrice)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#003063" }}>
                Payment History
              </Typography>
              {paymentHistory.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Method</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Amount</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Reference #</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Entered By</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Notes</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentHistory.map((pmt) => (
                        <TableRow key={`${pmt.type}-${pmt.id}`}>
                          <TableCell>{orderDetailDateTime(pmt.date)}</TableCell>
                          <TableCell>
                            <Chip
                              label={pmt.method}
                              size="small"
                              variant="outlined"
                              color={pmt.type === "online" ? "primary" : "default"}
                            />
                            {pmt.cardLastFour && (
                              <Typography variant="caption" sx={{ ml: 1 }}>
                                ****{pmt.cardLastFour}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                            {moneyFormat(pmt.amount)}
                          </TableCell>
                          <TableCell>{pmt.referenceNumber || "—"}</TableCell>
                          <TableCell>{pmt.enteredBy || "—"}</TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 180 }} title={pmt.notes}>
                              {pmt.notes || "—"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                  No payments recorded yet.
                </Typography>
              )}
            </Paper>

            {paymentHistory.length === 0 && orderItems.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No line items or payments on file for this order.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
        {attendeeId && Number(summary?.remainingBalance) > 0 && (
          <Button
            onClick={handleApplyPayment}
            variant="contained"
            sx={{ bgcolor: "#003063", "&:hover": { bgcolor: "#002145" } }}
            size="small"
            startIcon={<PaymentIcon />}
          >
            Apply Payment
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const MyReserved = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();

  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // View Order modal state
  const [orderModal, setOrderModal] = useState({ open: false, orderId: null, attendeeId: null });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const resp = await axiosPrivate.get("/api/my-reserved");
        const rows = Array.isArray(resp.data) ? resp.data : [];

        // Deduplicate: one row per orderId; rows without an orderId kept as-is
        const seenOrderIds = new Set();
        const deduplicated = [];
        for (const row of rows) {
          if (row.orderId != null) {
            if (!seenOrderIds.has(row.orderId)) {
              seenOrderIds.add(row.orderId);
              deduplicated.push(row);
            }
          } else {
            deduplicated.push(row);
          }
        }

        if (!cancelled) {
          setAllData(deduplicated);
          setFilteredData(deduplicated);
        }
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [axiosPrivate]);

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    const next = (allData || []).filter((row) => {
      if (!term) return true;
      return (
        String(row?.eventTitle ?? "").toLowerCase().includes(term) ||
        String(row?.teamName ?? "").toLowerCase().includes(term) ||
        String(row?.contactEmail ?? "").toLowerCase().includes(term) ||
        String(row?.orderId ?? "").includes(term)
      );
    });
    setFilteredData(next);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchTerm, allData]);

  const handleInvoice = useCallback(
    async (row) => {
      const orderId = row?.orderId;
      const attendeeId = row?.attendeeId;
      if (orderId) {
        try {
          const resp = await axiosPrivate.get(`/api/orders/${orderId}/invoice-pdf`, {
            responseType: "blob",
          });
          const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
          const link = document.createElement("a");
          link.href = url;
          link.download = `invoice-${orderId}.pdf`;
          link.click();
          window.URL.revokeObjectURL(url);
        } catch {
          if (attendeeId) navigate(`/invoices/${attendeeId}`);
          else enqueueSnackbar("Could not load invoice", { variant: "error" });
        }
      } else if (attendeeId) {
        navigate(`/invoices/${attendeeId}`);
      } else {
        enqueueSnackbar("No invoice available", { variant: "info" });
      }
    },
    [axiosPrivate, navigate, enqueueSnackbar]
  );

  const fmtDate = (v) => {
  if (!v) return null;
  const d = DateTime.fromISO(String(v), { zone: 'utc' });
  return d.isValid ? d.toFormat('MM/dd/yyyy') : String(v);
};

  const columns = useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 130,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        getActions: (params) => {
          const row = params?.row;
          const balance = Number(row?.balance ?? 0);
          const hasBalance = !Number.isNaN(balance) && balance > 0;
          const hasInvoices = Boolean(row?.hasInvoices);
          const orderId = row?.orderId;
          const attendeeId = row?.attendeeId;

          return [
            <Tooltip key="view" title={hasInvoices ? "View Order" : "No order record found"} arrow placement="top">
              <span>
                <GridActionsCellItem
                  icon={<ViewIcon />}
                  label="View Order"
                  disabled={!hasInvoices}
                  showInMenu={false}
                  onClick={() =>
                    setOrderModal({ open: true, orderId, attendeeId })
                  }
                />
              </span>
            </Tooltip>,
            <Tooltip key="payment" title={!attendeeId ? "No attendee record" : !hasBalance ? "No balance due" : "Apply Payment"} arrow placement="top">
              <span>
                <GridActionsCellItem
                  icon={<PaymentIcon />}
                  label="Apply Payment"
                  disabled={!hasBalance || !attendeeId}
                  showInMenu={false}
                  onClick={() =>
                    navigate(`/makepayment/${attendeeId}`, {
                      state: orderId ? { orderId, fromMyReserved: true } : undefined,
                    })
                  }
                />
              </span>
            </Tooltip>,
            <Tooltip key="invoice" title={hasInvoices ? "Download Invoice" : "No invoice available"} arrow placement="top">
              <span>
                <GridActionsCellItem
                  icon={<InvoiceIcon />}
                  label="Download Invoice"
                  disabled={!hasInvoices}
                  showInMenu={false}
                  onClick={() => handleInvoice(row)}
                />
              </span>
            </Tooltip>,
          ];
        },
      },
      {
        field: "eventTitle",
        headerName: "Event Title",
        minWidth: 220,
        flex: 1.5,
        renderCell: (params) => {
          const eventId = params?.row?.eventId;
          const title = params?.value ?? "";
          if (!eventId) return <span>{title}</span>;
          return <Link to={`/events/details/${eventId}`}>{title}</Link>;
        },
      },
      {
  field: "arrival",
  headerName: "Date",
  minWidth: 250,
  flex: 0.8,
  renderCell: (params) => {
    const { arrival, departure } = params.row;
    const start = fmtDate(arrival);
    const end   = fmtDate(departure);

    if (!start && !end) return <Typography variant="body2">—</Typography>;

    const isSameDate = start === end;

    return (
      <Typography variant="body2">
        {isSameDate || !end
          ? start || end
          : `${start} - ${end}`}
      </Typography>
    );
  },
},
      {
        field: "teamName",
        headerName: "Team/Group Name",
        minWidth: 100,
        flex: 1,
        renderCell: (params) => {
          const teamName = params?.value ?? "";
          const registrationType = String(params?.row?.registrationType ?? "").toLowerCase();
          const eventId = params?.row?.eventId;
          if (teamName && eventId) {
            return (
              <span
                onClick={() =>
                  navigate(`/team-view/${eventId}/${encodeURIComponent(teamName)}`)
                }
                style={{ color: "#d32f2f", cursor: "pointer", textDecoration: "underline" }}
              >
                {teamName}
              </span>
            );
          }
          return <span>{teamName}</span>;
        },
      },
      {
        field: "orderId",
        headerName: "Order ID",
        minWidth: 50,
        flex: 0.5,
        renderCell: (params) => (
          <Typography variant="body2">{params.value ?? "—"}</Typography>
        ),
      },
      {
        field: "balance",
        headerName: "Amount Due",
        minWidth: 80,
        flex: 0.6,
        type: "number",
        renderCell: (params) => {
          const val = Number(params.value ?? 0);
          return (
            <Typography
              variant="body2"
              fontWeight={val > 0 ? "bold" : "normal"}
              color={val > 0 ? "error.main" : "text.primary"}
            >
              {moneyFormat(val)}
            </Typography>
          );
        },
      },
      {
        field: "isPaid",
        headerName: "Paid",
        minWidth: 90,
        flex: 0.5,
        sortable: false,
        renderCell: (params) => {
          const balance = Number(params?.row?.balance ?? 0);
          const paid = !Number.isNaN(balance) && balance <= 0;
          return paid ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Yes"
              color="success"
              size="small"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={<CancelIcon />}
              label="No"
              color="error"
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: "contactEmail",
        headerName: "Contact",
        minWidth: 200,
        flex: 1,
        renderCell: (params) => {
          const email = String(params?.value ?? "").trim();
          if (!email) return "";
          return (
            <a href={`mailto:${email}`} onClick={(e) => e.stopPropagation()}>
              {email}
            </a>
          );
        },
      },
    ],
    [navigate, handleInvoice]
  );

  if (isLoading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
        <Typography className={styles.loadingText}>Loading your registrations...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.errorContainer}>
        <Typography color="error">
          Error occurred while fetching your registered events. Please contact an admin.
        </Typography>
      </Box>
    );
  }

  const totalCount = filteredData.length;
  const pageCount = Math.ceil(totalCount / paginationModel.pageSize);
  const startIndex = paginationModel.page * paginationModel.pageSize;
  const endIndex = startIndex + paginationModel.pageSize;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  return (
    <Box className={styles.container}>
      <Paper className={styles.searchPaper} sx={{ backgroundColor: "#f8f9fa" }}>
        <Box className={styles.searchContainer}>
          <TextField
            placeholder="Search by event, team, order ID, or contact email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <ClearIcon
                    className={styles.clearIcon}
                    onClick={() => setSearchTerm("")}
                  />
                </InputAdornment>
              ) : null,
            }}
            className={styles.searchField}
          />
        </Box>
      </Paper>

      <Paper className={styles.dataGridPaper}>
        <DataGrid
          rows={paginatedData}
          columns={columns}
          getRowId={(row) =>
            `${row.attendeeId ?? "att"}-${row.eventId ?? "evt"}-${row.orderId ?? "ord"}`
          }
          autoHeight
          disableSelectionOnClick
          disableMultipleSelection
          disableColumnFilter
          disableDensitySelector
          hideFooterPagination
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4, gap: 0.5 }}>
                <Typography sx={{ fontWeight: 600, color: '#21252C' }}>No reservations found</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>You don't have any reservations yet.</Typography>
              </Box>
            ),
          }}
          className="myReservedDataGrid"
        />
      </Paper>

      <Paper className={styles.paginationPaper}>
        <Stack
          className={styles.paginationStack}
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ width: "100%" }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {totalCount > 0 ? startIndex + 1 : 0} to{" "}
            {Math.min(endIndex, totalCount)} of {totalCount} registrations
          </Typography>

          <Stack className={styles.paginationControls} direction="row" alignItems="center">
            <Pagination
              count={pageCount}
              page={paginationModel.page + 1}
              onChange={(_, page) =>
                setPaginationModel((prev) => ({ ...prev, page: page - 1 }))
              }
              color="primary"
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
              className="myReservedPagination"
            />
          </Stack>

          <Box className={styles.pageSizeSelector} sx={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              Show:
            </Typography>
            <TextField
              select
              size="small"
              value={paginationModel.pageSize}
              onChange={(e) => {
                const newLimit = parseInt(e.target.value, 10);
                setPaginationModel((prev) => ({ ...prev, pageSize: newLimit, page: 0 }));
              }}
              SelectProps={{ native: true }}
              inputProps={{ style: { fontSize: "0.875rem" } }}
              className={`${styles.pageSizeField} myReservedPageSizeField`}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </TextField>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              per page
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Order Detail Modal */}
      <OrderDetailModal
        open={orderModal.open}
        orderId={orderModal.orderId}
        attendeeId={orderModal.attendeeId}
        onClose={() => setOrderModal({ open: false, orderId: null, attendeeId: null })}
      />
    </Box>
  );
};

export default MyReserved;
